import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/ui/card";
import { Badge } from "./ui/ui/badge";
import { Button } from "./ui/ui/button";
import { Input } from "./ui/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/ui/accordion";
import {
  Search,
  Filter,
  ExternalLink,
  BookOpen,
  Play,
  Wrench,
  FileText,
  Calendar,
  ChevronDown,
} from "lucide-react";
import { cn } from "../lib/utils";

interface Resource {
  title: string;
  description: string;
  difficulty?: "Beginner" | "Intermediate" | "Advanced";
  type: "Documentation" | "Tutorial" | "Tool" | "Other";
  url: string;
}

interface MeetupResources {
  date: string;
  title: string;
  resources: Resource[];
}

const ResourcesPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<string>("");

  const meetupResources: MeetupResources[] = [
    {
      date: "June 5, 2025",
      title: "Hack AI June 5th",
      resources: [
        {
          title: "Hack AI June 5th Presentation",
          description:
            "Main presentation covering the latest developments and topics discussed at the June 5th HackAI meetup",
          type: "Documentation",
          difficulty: "Intermediate",
          url: "https://docs.google.com/presentation/d/1UkUEoaqaoaJB8Epk5ZlF9BGCM6tzDW3E7EshYsBehII/edit?usp=sharing",
        },
        {
          title: "June 5th Activity Guide",
          description:
            "Step-by-step activity guide for hands-on exercises and tasks from the June 5th meetup",
          type: "Tutorial",
          difficulty: "Intermediate",
          url: "https://docs.google.com/document/d/1uDCmMl7dx1Z-PL8NQWmZn-umBBa5vVN7TAd4Vi6l2vw/edit?usp=sharing",
        },
        {
          title: "How much better can AI get?",
          description:
            "Exploration of the future potential and limitations of AI development, examining theoretical and practical boundaries",
          type: "Documentation",
          difficulty: "Intermediate",
          url: "https://drive.google.com/file/d/1vSrxcKlxKWsrhpLWiqoTqDghmDDgY18d/view?usp=sharing",
        },
      ],
    },
    {
      date: "May 1, 2025",
      title: "Coding effectively with LLMs",
      resources: [
        {
          title: "Building Apps with Cursor & LLMs Presentation",
          description:
            "A comprehensive presentation on how to effectively code and build applications using LLMs and Cursor IDE",
          type: "Documentation",
          difficulty: "Intermediate",
          url: "https://docs.google.com/presentation/d/1cn6dhLplt2pXfCgYDgwmxm5DTMqcDn01UF59cdGnNVo/edit?usp=sharing",
        },
        {
          title: "LLM Project Guide Repository",
          description:
            "Example project repository demonstrating best practices for building applications with LLMs",
          type: "Tutorial",
          difficulty: "Intermediate",
          url: "https://github.com/19bmiles/llm-project-guide",
        },
        {
          title: "Prompt Rules Grader Activity Guide",
          description:
            "Step-by-step guide for completing the prompt rules grading activity",
          type: "Tutorial",
          difficulty: "Beginner",
          url: "https://docs.google.com/document/d/1GhB-cfViZ31RL7_BNBlpeyAXCvpY-npUscBpiUgrU-4/edit?usp=sharing",
        },
      ],
    },
    {
      date: "September 5, 2024",
      title: "Voice AI Projects with Sindarin",
      resources: [
        {
          title: "Create a persona that you can call and have a convo with",
          description:
            "Guide to create various AI personas. Then to deploy them to be callable through Twilio.",
          type: "Tutorial",
          difficulty: "Beginner",
          url: "https://github.com/19bmiles/hackai-sindarin-templates",
        },
        {
          title: "Advanced Sindarin Burgers Tutorial",
          description:
            "Guide to extend functionality of a basic burger restaurant cashier AI persona",
          type: "Tutorial",
          difficulty: "Advanced",
          url: "https://github.com/19bmiles/sindarin-burgers-hackai",
        },
        {
          title: "Sindarin Platform",
          description: "Platform for creating voice AI personas",
          type: "Tool",
          url: "https://sindarin.com",
        },
      ],
    },
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "Documentation":
        return FileText;
      case "Tutorial":
        return Play;
      case "Tool":
        return Wrench;
      default:
        return BookOpen;
    }
  };

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case "Beginner":
        return "text-terminal-green";
      case "Intermediate":
        return "text-neon-blue";
      case "Advanced":
        return "text-cyber-purple";
      default:
        return "text-muted-foreground";
    }
  };

  const ResourceItem = ({ resource }: { resource: Resource }) => {
    const IconComponent = getTypeIcon(resource.type);

    return (
      <Card className="bg-card/20 border-border/30 hover:border-primary/50 transition-all duration-300 group">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <IconComponent className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {resource.title}
                  </a>
                </h3>
                <ExternalLink className="h-4 w-4 text-muted-foreground ml-2 flex-shrink-0" />
              </div>
              <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                {resource.description}
              </p>
              <div className="flex items-center space-x-2">
                <Badge variant="outline" className="text-xs">
                  {resource.type}
                </Badge>
                {resource.difficulty && (
                  <Badge
                    variant="secondary"
                    className={cn(
                      "text-xs",
                      getDifficultyColor(resource.difficulty)
                    )}
                  >
                    {resource.difficulty}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const filteredMeetups = meetupResources
    .filter((meetup) => !selectedEvent || meetup.title.includes(selectedEvent))
    .map((meetup) => ({
      ...meetup,
      resources: meetup.resources.filter(
        (resource) =>
          resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          resource.description.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    }))
    .filter((meetup) => meetup.resources.length > 0);

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="container max-w-6xl mx-auto space-y-12">
        {/* Header */}
        <div className="text-center space-y-4">
          <Badge variant="outline" className="border-primary text-primary">
            <BookOpen className="h-3 w-3 mr-1" />
            Resources
          </Badge>
          <h1 className="text-4xl lg:text-5xl font-bold font-mono text-primary">
            ./resources --search
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Access presentations, tutorials, and tools from all our HackAI
            meetups. Search and filter to find exactly what you need.
          </p>
        </div>

        {/* Search and Filter */}
        <Card className="bg-card/30 backdrop-blur-sm border-border/50">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 font-mono bg-background/50"
                />
              </div>
              <div className="relative sm:w-64">
                <Filter className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <select
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 bg-background/50 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring font-mono"
                >
                  <option value="">All Events</option>
                  {meetupResources.map((meetup, index) => (
                    <option key={index} value={meetup.title}>
                      {meetup.title}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <span>
              Found{" "}
              {filteredMeetups.reduce(
                (acc, meetup) => acc + meetup.resources.length,
                0
              )}{" "}
              resources
            </span>
            {(searchTerm || selectedEvent) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedEvent("");
                }}
                className="text-xs"
              >
                Clear filters
              </Button>
            )}
          </div>

          {/* Resources by Meetup */}
          <Accordion
            type="multiple"
            className="space-y-4"
            defaultValue={["item-0"]}
          >
            {filteredMeetups.map((meetup, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-border/50 rounded-lg bg-card/20 backdrop-blur-sm"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-primary/5">
                  <div className="flex items-center space-x-3 text-left">
                    <Calendar className="h-5 w-5 text-primary" />
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">
                        {meetup.title}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {meetup.date} • {meetup.resources.length} resources
                      </p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    {meetup.resources.map((resource, resourceIndex) => (
                      <ResourceItem key={resourceIndex} resource={resource} />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {filteredMeetups.length === 0 && (
            <Card className="bg-card/30 backdrop-blur-sm border-border/50 text-center">
              <CardContent className="py-12">
                <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  No resources found
                </h3>
                <p className="text-muted-foreground">
                  Try adjusting your search terms or filters
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Legend */}
        <Card className="bg-card/30 backdrop-blur-sm border-border/50">
          <CardHeader>
            <CardTitle className="text-sm font-mono text-primary">
              Legend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4 text-primary" />
                <span>Documentation</span>
              </div>
              <div className="flex items-center space-x-2">
                <Play className="h-4 w-4 text-primary" />
                <span>Tutorial</span>
              </div>
              <div className="flex items-center space-x-2">
                <Wrench className="h-4 w-4 text-primary" />
                <span>Tool</span>
              </div>
              <div className="flex items-center space-x-2">
                <BookOpen className="h-4 w-4 text-primary" />
                <span>Other</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border/30">
              <div className="flex items-center space-x-6 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-terminal-green"></div>
                  <span>Beginner</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-neon-blue"></div>
                  <span>Intermediate</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-cyber-purple"></div>
                  <span>Advanced</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResourcesPage;
